import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import { useAuthStore } from '@/store/authStore';
import { updateProfileSchema, addDependentSchema, contactChangeSchema, type UpdateProfileFormData, type AddDependentFormData, type ContactChangeFormData } from '@/utils/validators';
import { formatDate, formatPhone } from '@/utils/formatters';
import authService from '@/services/authService';

// Placeholder - will be replaced with actual user service
const fetchUserProfile = async () => {
  return authService.getProfile();
};

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddDependentModalOpen, setIsAddDependentModalOpen] = useState(false);
  const [isContactChangeModalOpen, setIsContactChangeModalOpen] = useState(false);

  // We can use the profile data from the query to ensure we have the latest data including dependents
  const { data: profileUser, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: fetchUserProfile,
    initialData: user, // Use store user as initial data
  });

  // Use the freshest user data
  const currentUser = profileUser || user;

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileFormData) => authService.updateProfile(data),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast.success('Profil mis à jour!');
      setIsEditModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la mise à jour');
    },
  });

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    formState: { errors: editErrors },
  } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      phone: currentUser?.phone,
    },
  });

  const {
    register: registerDependent,
    handleSubmit: handleDependentSubmit,
    formState: { errors: dependentErrors },
    reset: resetDependentForm,
  } = useForm<AddDependentFormData>({
    resolver: zodResolver(addDependentSchema),
  });

  const {
    register: registerContactChange,
    handleSubmit: handleContactChangeSubmit,
    formState: { errors: contactChangeErrors },
    reset: resetContactChangeForm,
  } = useForm<ContactChangeFormData>({
    resolver: zodResolver(contactChangeSchema),
  });

  const addDependentMutation = useMutation({
    mutationFn: (data: AddDependentFormData) => authService.addDependent(data),
    onSuccess: () => {
      toast.success('Dépendant ajouté avec succès!');
      resetDependentForm();
      setIsAddDependentModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de l\'ajout du dépendant');
    },
  });

  const requestContactChangeMutation = useMutation({
    mutationFn: (data: ContactChangeFormData) => authService.requestContactChange(data),
    onSuccess: () => {
      toast.success('Demande de modification envoyée avec succès!');
      resetContactChangeForm();
      setIsContactChangeModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de l\'envoi de la demande');
    },
  });

  const onEditProfile = (data: UpdateProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const onAddDependent = (data: AddDependentFormData) => {
    addDependentMutation.mutate(data);
  };

  const onRequestContactChange = (data: ContactChangeFormData) => {
    requestContactChangeMutation.mutate(data);
  };

  if (isLoading || !currentUser) {
     return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <AppShell title="Mon Profil">
      <div className="flex flex-col px-4 md:px-0 pt-6 gap-6">
            
            {/* Profile Header */}
            <div className="flex flex-col items-center gap-4">
                <div className="relative group cursor-pointer" onClick={() => setIsEditModalOpen(true)}>
                    <div 
                        className="size-28 rounded-full bg-gray-200 dark:bg-gray-700 bg-center bg-cover border-4 border-surface-light dark:border-surface-dark shadow-lg" 
                        style={{
                            backgroundImage: currentUser?.profilePictureUrl 
                              ? `url('${currentUser.profilePictureUrl}')` 
                              : 'none'
                        }}
                    >
                         {!currentUser?.profilePictureUrl && (
                            <div className="flex items-center justify-center w-full h-full text-3xl font-bold text-gray-500">
                                {currentUser?.firstName?.[0]}{currentUser?.lastName?.[0]}
                            </div>
                         )}
                    </div>
                    <div className="absolute bottom-0 right-0 bg-primary rounded-full p-1.5 border-4 border-surface-light dark:border-surface-dark flex items-center justify-center shadow-sm">
                        <MaterialIcon icon="edit" className="text-background-dark text-[18px]" />
                    </div>
                </div>
                <div className="text-center space-y-1">
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                        {currentUser?.firstName} {currentUser?.lastName}
                    </h2>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 dark:bg-primary/20 px-3 py-1 text-sm font-medium text-green-800 dark:text-green-300">
                        <span className="size-1.5 rounded-full bg-primary animate-pulse"></span>
                        {currentUser?.role === 'adherent' ? 'Adhérent Principal' : 'Bénéficiaire'}
                    </span>
                </div>
            </div>

            {/* Identification */}
            <section className="flex flex-col gap-3">
                <div className="px-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Identification</h3>
                    <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                        <MaterialIcon icon="lock" className="text-[14px]" />
                        Privé
                    </span>
                </div>
                <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                    {/* Member ID */}
                    <div className="relative flex items-center gap-4 p-4 border-b border-gray-100 dark:border-gray-800/50">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary/5 text-primary">
                            <MaterialIcon icon="badge" className="text-[24px]" />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Numéro adhérent</span>
                            <span className="text-base font-semibold text-slate-900 dark:text-white tracking-wide">
                                {currentUser?.memberNumber || 'N/A'}
                            </span>
                        </div>
                        <button className="p-2 text-gray-400 hover:text-primary transition-colors">
                            <MaterialIcon icon="content_copy" className="text-[20px]" />
                        </button>
                    </div>
                    {/* Date of Birth */}
                    <div className="relative flex items-center gap-4 p-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            <MaterialIcon icon="calendar_month" className="text-[24px]" />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Date de naissance</span>
                            <span className="text-base font-medium text-slate-900 dark:text-white">
                                {currentUser?.dateOfBirth ? formatDate(currentUser.dateOfBirth) : '-'}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Information */}
            <section className="flex flex-col gap-3">
                <div className="px-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Coordonnées</h3>
                </div>
                <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-800/50">
                    {/* Phone */}
                    <div className="flex items-center gap-4 p-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            <MaterialIcon icon="call" className="text-[24px]" />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Téléphone</span>
                            <span className="text-base font-medium text-slate-900 dark:text-white">
                                {formatPhone(currentUser?.phone || '')}
                            </span>
                        </div>
                    </div>
                    {/* Email */}
                    <div className="flex items-center gap-4 p-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            <MaterialIcon icon="mail" className="text-[24px]" />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Email</span>
                            <span className="text-base font-medium text-slate-900 dark:text-white break-all">
                                {currentUser?.email || user?.email}
                            </span>
                        </div>
                    </div>
                    {/* Address */}
                    <div className="flex items-center gap-4 p-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                             <MaterialIcon icon="location_on" className="text-[24px]" />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Adresse</span>
                            <span className="text-base font-medium text-slate-900 dark:text-white">
                                {currentUser?.address ? `${currentUser.address}, ${currentUser.city}` : 'Non renseigné'}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contract Information */}
             <section className="flex flex-col gap-3">
                <div className="px-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Contrat</h3>
                </div>
                {currentUser?.contract ? (
                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                        <div className="relative flex items-center gap-4 p-4">
                            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                                <MaterialIcon icon="verified_user" className="text-[24px]" />
                            </div>
                            <div className="flex flex-col flex-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                    {currentUser.contract.policyNumber}
                                </span>
                                <span className="text-base font-semibold text-slate-900 dark:text-white">
                                    {currentUser.contract.name}
                                </span>
                                <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1 mt-0.5">
                                    <span className="size-1.5 rounded-full bg-green-500"></span>
                                    {currentUser.contract.status}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-center text-gray-500 text-sm">
                        Aucun contrat actif
                    </div>
                )}
            </section>

             {/* Family Information */}
            <section className="flex flex-col gap-3">
                <div className="px-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Famille</h3>
                    {currentUser?.role === 'adherent' && (
                         <button 
                            onClick={() => setIsAddDependentModalOpen(true)}
                            className="text-xs font-medium text-primary hover:text-green-600 transition-colors flex items-center gap-0.5"
                         >
                            <MaterialIcon icon="add" className="text-[16px]" />
                            Ajouter
                        </button>
                    )}
                </div>
                <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-800/50">
                    {/* Current User */}
                     <div className="flex items-center gap-4 p-4 bg-gray-50/50 dark:bg-gray-800/20">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary font-bold text-sm">
                            {currentUser?.firstName?.[0]}{currentUser?.lastName?.[0]}
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-sm font-medium text-slate-900 dark:text-white">
                                {currentUser?.firstName} {currentUser?.lastName}
                            </span>
                            <span className="text-xs text-gray-500">Moi (Adhérent)</span>
                        </div>
                    </div>

                    {/* Dependents */}
                    {currentUser?.dependents && currentUser.dependents.length > 0 ? (
                        currentUser.dependents.map((dependent: any) => (
                            <div key={dependent.id} className="flex items-center gap-4 p-4">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold text-sm">
                                    {dependent.firstName?.[0]}{dependent.lastName?.[0]}
                                </div>
                                <div className="flex flex-col flex-1">
                                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                                        {dependent.firstName} {dependent.lastName}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {dependent.relationshipType === 'spouse' ? 'Conjoint(e)' : 'Enfant'} 
                                        {' • '} 
                                        {dependent.dateOfBirth ? `${new Date().getFullYear() - new Date(dependent.dateOfBirth).getFullYear()} ans` : ''}
                                    </span>
                                </div>
                                <MaterialIcon icon="chevron_right" className="text-gray-300" />
                            </div>
                        ))
                    ) : (
                         <div className="p-8 text-center text-gray-500 text-sm">
                            Aucun membre de famille ajouté
                        </div>
                    )}
                </div>
            </section>

            {/* Preferences */}
            <section className="flex flex-col gap-3">
                <div className="px-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Préférences</h3>
                </div>
                <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                    {/* Language */}
                    <div className="flex items-center gap-4 p-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                             <MaterialIcon icon="translate" className="text-[24px]" />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Langue</span>
                             <span className="text-base font-medium text-slate-900 dark:text-white">Français</span>
                        </div>
                        <MaterialIcon icon="chevron_right" className="text-gray-300" />
                    </div>
                </div>
            </section>

            {/* Edit Action */}
            <div className="mt-4">
                 <button 
                    onClick={() => setIsContactChangeModalOpen(true)}
                    className="w-full bg-primary hover:bg-green-400 active:bg-green-500 text-background-dark font-bold text-lg py-4 rounded-xl shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2"
                >
                    <MaterialIcon icon="edit_square" className="icon-filled" />
                    Modifier mes coordonnées
                 </button>
            </div>
            
            {/* Logout Action */}
            <div className="mt-2 text-center">
                 <button 
                    onClick={() => {
                        useAuthStore.getState().logout();
                        navigate('/login');
                    }}
                    className="text-red-500 font-medium text-sm hover:text-red-600 transition-colors py-2 px-4 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10"
                >
                    Se déconnecter
                 </button>
            </div>

      </div>

      {/* Edit Profile Modal (Hidden but functional) */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Modifier mon profil"
          footer={
            <>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleEditSubmit(onEditProfile)}
                isLoading={updateProfileMutation.isPending}
              >
                Enregistrer
              </Button>
            </>
          }
        >
          <form className="space-y-4">
            <Input
              {...registerEdit('firstName')}
              label="Prénom"
              error={editErrors.firstName?.message}
            />
            <Input
              {...registerEdit('lastName')}
              label="Nom"
              error={editErrors.lastName?.message}
            />
            <Input
              {...registerEdit('phone')}
              label="Téléphone"
              type="tel"
              error={editErrors.phone?.message}
            />
          </form>
        </Modal>

        {/* Add Dependent Modal */}
        <Modal
          isOpen={isAddDependentModalOpen}
          onClose={() => setIsAddDependentModalOpen(false)}
          title="Ajouter un dépendant"
          footer={
            <>
              <Button variant="outline" onClick={() => setIsAddDependentModalOpen(false)}>
                Annuler
              </Button>
              <Button 
                variant="primary" 
                onClick={handleDependentSubmit(onAddDependent)}
                isLoading={addDependentMutation.isPending}
              >
                Ajouter
              </Button>
            </>
          }
        >
          <form className="space-y-4">
            <Input
              {...registerDependent('firstName')}
              label="Prénom"
              error={dependentErrors.firstName?.message}
            />
            <Input
              {...registerDependent('lastName')}
              label="Nom"
              error={dependentErrors.lastName?.message}
            />
             <Input
              {...registerDependent('dateOfBirth')}
              label="Date de naissance"
              type="date"
              error={dependentErrors.dateOfBirth?.message}
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Lien de parenté
              </label>
              <select
                {...registerDependent('relationshipType')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner</option>
                <option value="spouse">Conjoint(e)</option>
                <option value="child">Enfant</option>
                <option value="other">Autre</option>
              </select>
              {dependentErrors.relationshipType && (
                <p className="mt-1 text-sm text-red-500">{dependentErrors.relationshipType.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Genre
              </label>
              <select
                {...registerDependent('gender')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner</option>
                <option value="male">Masculin</option>
                <option value="female">Féminin</option>
                <option value="other">Autre</option>
              </select>
            </div>
            <Input
              {...registerDependent('phone')}
              label="Téléphone"
              type="tel"
              error={dependentErrors.phone?.message}
            />
          </form>
        </Modal>

        {/* Contact Change Modal */}
        <Modal
          isOpen={isContactChangeModalOpen}
          onClose={() => setIsContactChangeModalOpen(false)}
          title="Demande de modification"
          footer={
            <>
              <Button variant="outline" onClick={() => setIsContactChangeModalOpen(false)}>
                Annuler
              </Button>
              <Button 
                variant="primary" 
                onClick={handleContactChangeSubmit(onRequestContactChange)}
                isLoading={requestContactChangeMutation.isPending}
              >
                Envoyer la demande
              </Button>
            </>
          }
        >
          <form className="space-y-4">
             <p className="text-sm text-gray-500 mb-4">
                 Pour modifier des informations sensibles (adresse, état civil), une demande doit être validée par nos services.
             </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Champ à modifier
              </label>
              <select
                {...registerContactChange('field')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner</option>
                <option value="phone">Téléphone</option>
                <option value="email">Email</option>
                <option value="address">Adresse</option>
              </select>
            </div>
            <Input
              {...registerContactChange('newValue')}
              label="Nouvelle valeur"
              error={contactChangeErrors.newValue?.message}
            />
            <Input
               {...registerContactChange('reason')}
               label="Raison (optionnel)"
               error={contactChangeErrors.reason?.message}
            />
          </form>
        </Modal>
    </AppShell>
  );
};

export default ProfilePage;
